import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  ChartFacade,
  ChartId,
  TreatmentPlanFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  ChartingHistorySelectorComponent,
  CurrentPatientScope,
} from '@principle-theorem/ng-principle-shared';
import {
  TypedFormControl,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import { toINamedDocuments } from '@principle-theorem/principle-core';
import {
  IChartHistoryItem,
  ITreatmentPlan,
  type ChartSection,
  type ChartView,
  type IClinicalChart,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  WithRef,
  isSameRef,
  patchDoc,
  snapshot,
  sortByCreatedAt,
} from '@principle-theorem/shared';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ChartSectionSelectorComponent } from '../chart-section-selector/chart-section-selector.component';
import { ChartViewSelectorComponent } from '../chart-view-selector/chart-view-selector.component';

@Component({
  selector: 'pr-chart-view-toolbar',
  templateUrl: './chart-view-toolbar.component.html',
  styleUrls: ['./chart-view-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatLabel,
    ReactiveFormsModule,
    ChartingHistorySelectorComponent,
    ChartViewSelectorComponent,
    ChartSectionSelectorComponent,
  ],
})
export class ChartViewToolbarComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  section$: Observable<ChartSection>;
  view$: Observable<ChartView>;
  chart$: Observable<WithRef<IClinicalChart> | IClinicalChart>;
  chartHistory$ = new ReplaySubject<IChartHistoryItem[]>(1);
  treatmentPlans$: Observable<INamedDocument<ITreatmentPlan>[]>;
  treatmentPlanCtrl = new TypedFormControl<
    INamedDocument<ITreatmentPlan> | undefined
  >(undefined);
  @Input() showHistorySelector = true;

  @Input()
  set chartHistory(chartHistory: IChartHistoryItem[]) {
    if (chartHistory) {
      this.chartHistory$.next(chartHistory);
    }
  }

  constructor(
    private _chartStore: ChartFacade,
    private _patientScope: CurrentPatientScope,
    private _treatmentPlanFacade: TreatmentPlanFacade
  ) {
    this.section$ = this._chartStore.chartSection$(ChartId.InAppointment);
    this.view$ = this._chartStore.chartView$(ChartId.InAppointment);
    this.chart$ = this._chartStore.clinicalChartState$(ChartId.InAppointment);
    this.treatmentPlans$ = this._treatmentPlanFacade.treatmentPlans$.pipe(
      map((plans) => plans.sort(sortByCreatedAt)),
      toINamedDocuments()
    );

    formControlChanges$(this.treatmentPlanCtrl)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((plan) =>
        this._chartStore.setSelectedPlan(ChartId.InAppointment, plan?.ref)
      );
  }

  async selectView(view: ChartView): Promise<void> {
    if (view) {
      const selectedPatient = await snapshot(this._patientScope.doc$);
      if (selectedPatient) {
        await patchDoc(selectedPatient.ref, {
          settings: {
            charting: {
              view,
            },
          },
        });
      }
      this._chartStore.setChartView(ChartId.InAppointment, view);
      this._chartStore.setSelectedSurfaces(ChartId.InAppointment, []);
    }
  }

  selectSection(section: ChartSection): void {
    if (section) {
      this._chartStore.setChartSection(ChartId.InAppointment, section);
      this._chartStore.setSelectedSurfaces(ChartId.InAppointment, []);
    }
  }

  selectChart(chart: WithRef<IClinicalChart>): void {
    this._chartStore.setChart(ChartId.InAppointment, chart);
  }

  compareWithFn(
    aRef?: INamedDocument<ITreatmentPlan>,
    bRef?: INamedDocument<ITreatmentPlan>
  ): boolean {
    return isSameRef(aRef, bRef);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
